import type {MouseEvent, ReactNode} from 'react'
import {clsx} from 'clsx'
import styles from './ReferenceChip.module.css'

export interface ReferenceChipProps {
  /**
   * Optional visual rendered before the label (e.g. an octicon). Always
   * wrapped in an `aria-hidden` container; the chip's accessible name comes
   * from `label` alone.
   */
  leadingVisual?: ReactNode
  /**
   * The chip's display text and accessible name.
   */
  label: string
  wrapLabel?: boolean
  /**
   * When provided, the chip renders as a link to this URL.
   */
  href?: string
  /**
   * Called when the chip is activated. The chip renders as a `<button>` when
   * this is provided without `href`.
   */
  onClick?: (event: MouseEvent<HTMLElement>) => void
  /**
   * Optional visual rendered after the label (e.g. a line range or remove
   * button). Not aria-hidden, so pass accessible content directly (e.g. a
   * labeled button) when it needs to be reachable by assistive technology.
   */
  trailingVisual?: ReactNode
}

/**
 * A small, pure token for referencing a file, symbol, or other attachment.
 * Renders as a link when `href` is set, a button when `onClick` is set, or a
 * static span otherwise. No context, no data fetching, no knowledge of what
 * kind of thing it's referencing; callers pass display-ready label/visuals.
 */
export function ReferenceChip({
  leadingVisual,
  label,
  wrapLabel = false,
  href,
  onClick,
  trailingVisual,
}: ReferenceChipProps) {
  const isInteractive = Boolean(href || onClick)
  const content = (
    <>
      {leadingVisual ? (
        <span className={styles.leadingVisual} aria-hidden="true">
          {leadingVisual}
        </span>
      ) : null}
      <span className={styles.label}>{label}</span>
      {trailingVisual ? <span className={styles.trailingVisual}>{trailingVisual}</span> : null}
    </>
  )
  const className = clsx(styles.chip, isInteractive && styles.interactive, wrapLabel && styles.wrapLabel)

  if (href) {
    return (
      <a href={href} className={className} onClick={onClick}>
        {content}
      </a>
    )
  }

  if (onClick) {
    return (
      <button type="button" className={className} onClick={onClick}>
        {content}
      </button>
    )
  }

  return <span className={className}>{content}</span>
}
